import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import React from 'react';

import ImagePlaceholder from '../../assets/images/image-placeholder.jpg';
import {
  subscribeToImageResize,
  getImageResizeVersion,
  getResizedImageUrl,
  reportImageOutcome,
} from '../../utils/imageResize';
import './CdnImage.scss';

export interface CdnImageProps {
  classes?: {
    loading?: string;
    image?: string;
    root?: string;
  };
  isLoading?: boolean;
  className?: string;
  rounded?: boolean;
  height?: number;
  width?: number;
  src?: string;
  alt?: string;
  // Pass 'eager' for above-the-fold hero/cover images (LCP candidates).
  loading?: 'eager' | 'lazy';
  /**
   * The longest side to ask the asset domain for (see `getResizedImageUrl`).
   * The original is the fallback when the small version fails. Leave it out
   * for images shown large.
   */
  resizeMaxSide?: number;
}

export const CdnImage = ({
  alt,
  className,
  classes,
  height,
  isLoading,
  loading = 'lazy',
  resizeMaxSide,
  rounded = true,
  src,
  width,
}: CdnImageProps) => {
  // Loading state belongs to a URL, not to the component. A cached image can
  // fire `load` before any effect runs, so an effect that reset "loading" on
  // mount could cover an image that had already arrived, for good.
  const [loadedUrl, setLoadedUrl] = React.useState<string>();
  const [failedUrls, setFailedUrls] = React.useState<readonly string[]>([]);
  const [failuresSrc, setFailuresSrc] = React.useState(src);

  // A new source starts with no failures, as it always has. Reset during
  // render, so no frame shows the new source with the old failures.
  if (failuresSrc !== src) {
    setFailuresSrc(src);
    setFailedUrls([]);
  }

  // Re-render when resizing switches off for this page (see imageResize.ts)
  React.useSyncExternalStore(subscribeToImageResize, getImageResizeVersion);

  const imageUrl = [getResizedImageUrl(src, resizeMaxSide), src]
    .filter((url): url is string => Boolean(url))
    .find((url) => !failedUrls.includes(url));
  const hasError = Boolean(src) && !imageUrl;

  const showSkeleton = isLoading ?? Boolean(imageUrl && loadedUrl !== imageUrl);

  const markLoaded = React.useCallback((url: string) => {
    reportImageOutcome(url, 'loaded');
    setLoadedUrl(url);
  }, []);

  const markFailed = React.useCallback((url: string) => {
    reportImageOutcome(url, 'failed');
    setFailedUrls((urls) => (urls.includes(url) ? urls : [...urls, url]));
  }, []);

  // React drops a `load` or `error` fired before the element is committed,
  // which a cached image can do during a time-sliced render. So an image that
  // is already complete when it mounts settles here. Complete with no pixels
  // means broken, and only then does `decode()` reject (measured in Chromium
  // and WebKit, 04.11: a lazy image not yet loaded is not `complete`).
  const settleIfComplete = React.useCallback(
    (image: HTMLImageElement | null) => {
      const url = image?.getAttribute('src');
      if (!url || !image?.complete) return;
      if (image.naturalWidth > 0) markLoaded(url);
      else if ('decode' in image) {
        image.decode().then(
          () => markLoaded(url),
          () => markFailed(url),
        );
      }
    },
    [markFailed, markLoaded],
  );

  const styles = React.useMemo(
    () => ({
      height: height && height >= 0 ? height : '100%',
      width: width && width >= 0 ? width : '100%',
    }),
    [width, height],
  );

  return (
    <div
      className={classNames(
        'Mfw-CdnImage-root',
        {
          'Mfw-CdnImage-rounded': rounded,
        },
        className,
        classes?.root,
      )}
      style={styles}
    >
      {showSkeleton ? (
        <Skeleton
          className={classNames('Mfw-CdnImage-loading', classes?.loading)}
          variant="rectangular"
        />
      ) : null}
      {imageUrl && (
        // One element per URL: an event from the small version can never be
        // read as the original's, and a fallback starts a fresh request.
        <img
          className={classNames('Mfw-CdnImage-image', classes?.image)}
          onError={() => markFailed(imageUrl)}
          onLoad={() => markLoaded(imageUrl)}
          ref={settleIfComplete}
          loading={loading}
          alt={alt ?? ''}
          src={imageUrl}
          key={imageUrl}
        />
      )}
      {hasError && (
        <img
          className={classNames('Mfw-CdnImage-image', classes?.image)}
          src={ImagePlaceholder}
          loading={loading}
          alt={alt ?? ''}
        />
      )}
    </div>
  );
};

CdnImage.displayName = 'CdnImage';

export default CdnImage;
